// Card-to-card morph from the card-transitions hand-off: two live card canvases
// blend through a drifting noise front with a faint glow along its edge.

import { createFullScreenProgram, drawFullScreen, loseGl } from './glProgram';

export const MORPH_VS =
  'attribute vec2 a;varying vec2 v;void main(){v=a*.5+.5;gl_Position=vec4(a,0.,1.);}';

export const MORPH_FS = `precision highp float;
varying vec2 v;
uniform sampler2D A,B;
uniform float p,t,asp;
float h(vec2 q){return fract(sin(dot(q,vec2(127.1,311.7)))*43758.5453);}
float n(vec2 q){vec2 i=floor(q),f=fract(q);f=f*f*(3.-2.*f);return mix(mix(h(i),h(i+vec2(1,0)),f.x),mix(h(i+vec2(0,1)),h(i+vec2(1,1)),f.x),f.y);}
float fbm(vec2 q){float s=0.,a=.5;for(int i=0;i<4;i++){s+=a*n(q);q=q*2.03+vec2(1.7,9.2);a*=.5;}return s;}
void main(){
  vec2 uv=v,q=vec2(uv.x*asp,uv.y)*2.4;float W=.17;
  float th=fbm(q+vec2(t*.2,0.))*.62+(uv.x*.7+(1.-uv.y)*.3)*.38;
  float m=smoothstep(th-W,th+W,p*(1.+2.*W)-W);float b=m*(1.-m)*4.;
  vec2 off=(vec2(fbm(q*1.25+3.1),fbm(q*1.25+8.7))-.5)*.2;
  vec3 ca=texture2D(A,clamp(uv+off*m,0.,1.)).rgb,cb=texture2D(B,clamp(uv-off*(1.-m),0.,1.)).rgb;
  vec3 c=mix(ca,cb,m);c+=vec3(.8,1.,.92)*pow(b,3.)*.3;
  gl_FragColor=vec4(c,1.);
}`;

export type Morpher = {
  canvas: HTMLCanvasElement;
  /** Draws one frame blending `from` → `to` at progress `p` (0–1); false if a source can't be read. */
  frame: (
    from: HTMLCanvasElement,
    to: HTMLCanvasElement,
    p: number,
    t: number,
  ) => boolean;
  resize: (w: number, h: number) => void;
  dispose: () => void;
};

/** WebGL morph renderer, or null when WebGL or the shader is unavailable. */
export const createMorpher = (): Morpher | null => {
  const canvas = document.createElement('canvas');
  const gl = canvas.getContext('webgl', {
    alpha: false,
    antialias: false,
    premultipliedAlpha: false,
  });
  if (!gl) return null;
  const prog = createFullScreenProgram(gl, MORPH_VS, MORPH_FS);
  if (!prog) {
    loseGl(gl);
    return null;
  }
  const U = {
    p: gl.getUniformLocation(prog, 'p'),
    t: gl.getUniformLocation(prog, 't'),
    asp: gl.getUniformLocation(prog, 'asp'),
  };
  const texture = (unit: number) => {
    const tex = gl.createTexture();
    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    return tex;
  };
  const tA = texture(0);
  const tB = texture(1);
  gl.uniform1i(gl.getUniformLocation(prog, 'A'), 0);
  gl.uniform1i(gl.getUniformLocation(prog, 'B'), 1);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);

  const upload = (
    unit: number,
    tex: WebGLTexture | null,
    src: HTMLCanvasElement,
  ) => {
    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, src);
  };

  return {
    canvas,
    resize: (w, h) => {
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }
      gl.viewport(0, 0, w, h);
      gl.uniform1f(U.asp, w / h);
    },
    frame: (from, to, p, t) => {
      try {
        upload(0, tA, from);
        upload(1, tB, to);
      } catch {
        // A tainted or lost source canvas can't be sampled.
        return false;
      }
      gl.uniform1f(U.p, p);
      gl.uniform1f(U.t, t);
      drawFullScreen(gl);
      return true;
    },
    dispose: () => loseGl(gl),
  };
};
